import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useReducer, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Formik, Form, FormikProps } from 'formik'
import * as yup from 'yup'
import { skipToken } from '@reduxjs/toolkit/query'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import SelectField from '@/components/formik/fields/SelectField'
import SelectInput from '@/components/ui/inputs/SelectInput'
import TextField from '@/components/formik/fields/TextField'

import { ExpertiseSpecialities } from '@/constants/specialities'

import { useIntro } from '@/hooks/useIntro'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { ExpertiseService, ExpertiseStatus } from '@/core/models/expertise.model'
import { useDeleteExpertiseMutation, useDraftExpertiseMutation, useGetExpertiseQuery, useUpdateExpertiseMutation, useSendExpertiseMutation } from '@/core/services/expertise'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { UpdateExpertiseIntro } from '@/utils/onboarding/intro'

import Documents from './containers/Documents'
import HealthDataList from './containers/HealthDataList'
import PatientInfosCard from './components/PatientInfosCard'
import SelectTranscriptionDialog from './containers/SelectTranscriptionDialog'
import Transcriptions from './containers/Transcriptions'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

import { ExpertiseValidationStepperContext, reducer as validationReducer } from './ExpertiseValidationPage'
import SelectDoctorStep from './containers/SelectDoctorStep'
import SelectNetworkStep from './containers/SelectNetworkStep'
import Tabs from '@/components/ui/tabs/Tabs'
import TabPanel from '@/components/ui/tab/TabPanel'

type UpdateExpertiseFormValues = {
  comment: string
  speciality: string
}

type ExpertiseUpdateDialogProps = {
  expertiseId?: string
  open: boolean
  onClose: () => void
}

export default function ExpertiseUpdateDialog(props: ExpertiseUpdateDialogProps) {
  const { open, onClose, expertiseId } = props
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  
  const isTooSmall = useIsMobile()
  const dir = isTooSmall ? "column" : "row"

  const formikRef = useRef<FormikProps<UpdateExpertiseFormValues>>(null)

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false)

  const authUser = useAppSelector(selectAuthUser)

  const { data: expertise, isLoading: isFetchingExpertise } = useGetExpertiseQuery(expertiseId ?? skipToken)
  const [deleteExpertise, { isLoading: isDeletingExpertise }] = useDeleteExpertiseMutation()
  const [draftExpertise, { isLoading: isDraftingExpertise }] = useDraftExpertiseMutation()
  const [updateExpertise, { isLoading: isUpdatingExpertise }] = useUpdateExpertiseMutation()
  const [sendExpertise, { isLoading: isSendingExpertise }] = useSendExpertiseMutation()

  const [validationState, validationDispatch] = useReducer(validationReducer, {
    activeStep: 0,
    initialValues: {},
    values: {},
  })

  const isDraft = expertise?.status === 'draft'
  const isExpertiseAssociatedWithExternalService = useMemo(() => _.includes([ExpertiseStatus.Omnidoc], expertise?.status), [expertise])

  const serviceOptions = useMemo(
    () =>
      Object.keys(ExpertiseService).map((key) => ({
        label: key,
        value: ExpertiseService[key as keyof typeof ExpertiseService],
      })),
    []
  )
  const [service, setService] = useState<string>(serviceOptions[0].value)

  const updateExpertiseFormInitialValues = useMemo(
    () => ({
      comment: expertise?.comment || '',
      speciality: expertise?.speciality.toUpperCase() || '',
    }),
    [expertise]
  )

  const updateExpertiseFormValidationSchema = useMemo(
    () =>
      yup.object().shape({
        comment: service === ExpertiseService.MedConnect && !isExpertiseAssociatedWithExternalService ? yup.string().required().min(3) : yup.string(),
        speciality: yup.string(),
      }),
    [isExpertiseAssociatedWithExternalService, service]
  )

  const handleDeleteExpertise = async () => {
    if (!expertiseId) return
    try {
      await deleteExpertise(expertiseId).unwrap()
      enqueueSnackbar(t('notification.expertise.delete.success'), { variant: 'success' })
      onClose()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.delete.error')), { variant: 'error' })
    }
  }

  const handleConfirmation = async (values: UpdateExpertiseFormValues) => {
    if (!expertiseId) return

    if (isDraft) {
      try {
        await draftExpertise({ id: expertiseId, comment: values.comment, speciality: values.speciality }).unwrap()
        validationDispatch({ type: 'next' }) // Move to validation step
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.draft.error')), { variant: 'error' })
      }
    } else {
      try {
        await updateExpertise({ id: expertiseId, speciality: values.speciality }).unwrap()
        onClose()
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.update.error')), { variant: 'error' })
      }
    }
  }

  const handleConfirm = async () => {
    if (!expertiseId) return
    try {
      if (validationState.values.medecin) {
        const expertise = await sendExpertise({ id: expertiseId, receiverId: validationState.values.medecin, receiverType: 'Staff' }).unwrap()
        const doctor = _.trim(`${expertise.medecin?.userInfo.name || ''} ${expertise.medecin?.userInfo.surname || ''}`)
        enqueueSnackbar(t('notification.expertise.send.successToDoctor', { doctor }), {
          variant: 'success',
        })
      } else if (validationState.values.network) {
        const expertise = await sendExpertise({ id: expertiseId, receiverId: validationState.values.network, receiverType: 'Network' }).unwrap()
        const network = expertise.network?.name ?? ''
        enqueueSnackbar(t('notification.expertise.send.successToNetwork', { network }), {
          variant: 'success',
        })
      }
      onClose()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.send.error')), { variant: 'error' })
    }
  }

  const handleCancel = () => {
    onClose()
  }

  const handleChangeService = (event: React.ChangeEvent<HTMLInputElement>) => {
    setService(event.target.value)
    formikRef.current?.setFieldTouched('comment', false, false)
  }

  const toggleDeleteConfirmationDialog = () => {
    setDeleteConfirmationDialogOpen((prevState) => !prevState)
  }

  useIntro(UpdateExpertiseIntro, !expertise)

  useEffect(() => {
    if (isExpertiseAssociatedWithExternalService && expertiseId) {
      onClose()
    }
  }, [isExpertiseAssociatedWithExternalService, expertiseId])

  // show loader when fetching expertise
  if (isFetchingExpertise) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  // show not found if expertise is not existed or authenticated user is not its creator
  if (!expertise || authUser?._id !== expertise.createdBy._id) {
    return <></>
  }

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="xl">
      <ExpertiseValidationStepperContext.Provider value={{
        activeStep: validationState.activeStep,
        handleBack: () => validationDispatch({ type: 'back' }),
        handleChange: (field, value) => validationDispatch({ type: 'setValues', payload: { [field]: value } }),
        handleNext: () => validationDispatch({ type: 'next' }),
        initialValues: validationState.initialValues,
        values: validationState.values,
      }}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h2" component="div" sx={{ fontSize: '24px', fontWeight: 700 }}>
              {t('page.expertise.title', { context: isDraft ? 'create' : 'update' })}
            </Typography>
            <IconButton onClick={handleCancel} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {validationState.activeStep === 0 && (
            <Stack sx={{ height: '100%' }}>
              {service === ExpertiseService.Omnidoc && (
                <Alert icon={false} severity="warning" sx={{ mb: 2 }} variant="outlined">
                  {t('page.expertise.omnidocWarningMessage')}
                </Alert>
              )}
              <Grid container spacing={5}>
                <Grid item xs={12} sm={7} md={8} xl={9}>
                  <Box sx={{ width: '100%', pt: '10px', pb: '20px' }}>
                    <Stack direction="column" spacing={5}>
                      {isDraft && (
                        <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2} id="service">
                          <Typography variant="h3" component="div" sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                            {t('page.expertise.service.title')}
                          </Typography>
                          <SelectInput onChange={handleChangeService} options={serviceOptions} value={service} />
                        </Stack>
                      )}
                      <HealthDataList id="patient-data" expertiseId={expertiseId}/>
                      <div id="expertise-document">
                        <Documents expertiseId={expertiseId}/>
                      </div>
                      {isDraft && <Transcriptions expertiseId={expertiseId}/>}
                      <Formik
                        innerRef={formikRef}
                        initialValues={updateExpertiseFormInitialValues}
                        onSubmit={handleConfirmation}
                        validationSchema={updateExpertiseFormValidationSchema}
                      >
                        {(formikProps) => (
                          <Form onSubmit={formikProps.handleSubmit} id="extra-infos">
                            <Stack spacing={3}>
                              <Stack justifyContent="flex-end" alignItems="start" spacing={2}>
                                <Typography variant="h3" component="div" sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                                  {t('page.expertise.speciality.title')}
                                </Typography>
                                <Box sx={{ width: '40%' }}>
                                  <SelectField
                                    fullWidth
                                    name="speciality"
                                    data-testid="speciality-select"
                                    options={_.map(ExpertiseSpecialities, (speciality) => ({
                                      label: speciality.toUpperCase(),
                                      value: speciality.toUpperCase(),
                                    }))}
                                  />
                                </Box>
                              </Stack>
                              {isDraft && (
                                <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2}>
                                  <Typography variant="h3" component="div" sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                                    {t('page.expertise.initialMessage.title')}
                                  </Typography>
                                  <Stack direction={dir} spacing={2} width="100%">
                                    <Box flex={1}>
                                      <TextField
                                        fullWidth
                                        inputProps={{ 'data-testid': 'comment-textarea' }}
                                        maxRows={6}
                                        minRows={4}
                                        multiline
                                        name="comment"
                                      />
                                    </Box>
                                    <Box>
                                      <SelectTranscriptionDialog
                                        onSelect={(sentence) => {
                                          formikProps.setFieldValue('comment', sentence)
                                        }}
                                      />
                                    </Box>
                                  </Stack>
                                </Stack>
                              )}
                            </Stack>
                          </Form>
                        )}
                      </Formik>
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={5} md={4} xl={3}>
                  <Box sx={{ position: 'sticky', top: 0, pt: '10px' }}>
                    <PatientInfosCard patient={expertise.patient} />
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          )}
          {validationState.activeStep === 1 && (
            <Stack height="100%">
              <Tabs
                centered
                tabs={[
                  { id: 'doctors', label: t('page.expertise.selectDoctorStep.title') },
                  { id: 'networks', label: t('page.expertise.selectDoctorStep.network.title') },
                ]}
                onChange={(tabValue) => {
                  if (tabValue === 0) {
                    validationDispatch({ type: 'setValues', payload: { network: undefined } })
                  } else if (tabValue === 1) {
                    validationDispatch({ type: 'setValues', payload: { medecin: undefined } })
                  }
                }}
                renderTabPanel={({ value }) => (
                  <Box sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
                    <TabPanel value={value} index={0} sx={{ height: '100%' }}>
                      <SelectDoctorStep />
                    </TabPanel>
                    <TabPanel value={value} index={1} sx={{ p: 4 }}>
                      <SelectNetworkStep />
                    </TabPanel>
                  </Box>
                )}
                variant="fullWidth"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" pt={1}>
            {validationState.activeStep === 0 && (
              <Stack direction="row" alignItems="center" justifyContent="end" spacing={3}>
                {!isDraft && (
                  <Button onClick={handleCancel} variant="neutral">
                    {t('common:button.cancel.label')}
                  </Button>
                )}
                <LoadingButton
                  color="primary"
                  loading={isDraftingExpertise || isUpdatingExpertise}
                  onClick={() => formikRef.current?.submitForm()}
                  variant="contained"
                >
                  {t('common:button:confirm.label')}
                </LoadingButton>
                {isDraft && (
                  <Button color="error" onClick={toggleDeleteConfirmationDialog} variant="contained">
                    {t('common:button:delete.label')}
                  </Button>
                )}
              </Stack>
            )}
            {validationState.activeStep === 1 && (
              <Stack direction="row" alignItems="center" justifyContent="end" spacing={3}>
                <Button onClick={() => validationDispatch({ type: 'back' })} variant="neutral">
                  {t('common:button.back.label')}
                </Button>
                <LoadingButton
                  color="primary"
                  disabled={!validationState.values.medecin && !validationState.values.network}
                  loading={isSendingExpertise}
                  onClick={handleConfirm}
                  variant="contained"
                >
                  {t('common:button.confirm.label')}
                </LoadingButton>
              </Stack>
            )}
          </Stack>
        </DialogActions>
        <DeleteConfirmationDialog
          isDeleting={isDeletingExpertise}
          onClose={toggleDeleteConfirmationDialog}
          onDelete={handleDeleteExpertise}
          open={deleteConfirmationDialogOpen}
          title={t('dialog.deleteConfirmation.expertise.title')}
          message={t('dialog.deleteConfirmation.expertise.message')}
        />
      </ExpertiseValidationStepperContext.Provider>
    </Dialog>
  )
}
